import { useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Eye, EyeOff, MoveLeft } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'

import LogoImg from '@/assets/images/logo.svg?url'
import { ROUTES } from '@/common/constants/routes'
import { useModalApplication } from '@/common/hooks/useModalApplication'
import { parseJwt } from '@/common/utils/parseJwt'
import { useNewPassword } from '../../hooks/useNewPassword'
import {
  NewPasswordForm,
  NewPasswordSchema,
} from '../../schemas/NewPasswordSchema'

export default function NewPassword() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [invalidToken, setInvalidToken] = useState(false)

  const [searchParams] = useSearchParams()
  const { t, i18n } = useTranslation(['login', 'common'])
  const navigate = useNavigate()
  const { handleModal, resetModal } = useModalApplication()
  const { handleNewPassword: newPassword, isPendingNewPassword: isPending } =
    useNewPassword()

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const token = searchParams.get('verify-token')

  const hookForm = useForm<NewPasswordForm>({
    resolver: zodResolver(NewPasswordSchema()),
    mode: 'onBlur',
    defaultValues: { password: '', confirmPassword: '' },
  })

  const handleNewPasswordSubmit = async (data: NewPasswordForm) => {
    if (!token) return

    await newPassword(token, data.password)
    timeoutRef.current = setTimeout(() => {
      navigate(ROUTES.LOGIN.SIGN_IN, { replace: true })
    }, 3000)
  }

  useEffect(() => {
    if (!token) {
      setInvalidToken(true)
      return
    }

    try {
      const decoded = parseJwt<{ locale?: string }>(token)
      if (decoded.locale && decoded.locale !== i18n.language) {
        i18n.changeLanguage(decoded.locale)
      }
    } catch {
      setInvalidToken(true)
    }
  }, [token, i18n])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!token || invalidToken) {
    return <Navigate to={ROUTES.LOGIN.SIGN_IN} replace />
  }

  return (
    <div className="flex flex-col gap-32 h-full w-full">
      <HeadProvider>
        <Title>{t('forgot_password.new_password.browser.title')}</Title>
      </HeadProvider>

      <img
        src={LogoImg}
        alt={t('sign_in.logo.alt')}
        className="w-1/4 mx-auto"
      />

      <FormProvider {...hookForm}>
        <form
          onSubmit={hookForm.handleSubmit(handleNewPasswordSubmit)}
          className="flex flex-col gap-6"
        >
          <div className="text-left mb-6">
            <h1 className="text-2xl font-semibold text-brand-900 mb-2">
              {t('forgot_password.new_password.title')}
            </h1>
            <p className="text-base-600 text-sm">
              {t('forgot_password.new_password.subtitle')}
            </p>
          </div>

          <Input.Icon
            size="lg"
            name="password"
            placeholder={t('forgot_password.new_password.password.placeholder')}
            icon={
              showPassword ? (
                <Eye
                  className="text-base-500 size-4 cursor-pointer"
                  onClick={() => setShowPassword(false)}
                />
              ) : (
                <EyeOff
                  className="text-base-500 size-4 cursor-pointer"
                  onClick={() => setShowPassword(true)}
                />
              )
            }
            type={showPassword ? 'text' : 'password'}
            iconPosition="right"
          />

          <Input.Icon
            size="lg"
            name="confirmPassword"
            placeholder={t(
              'forgot_password.new_password.confirm_password.placeholder',
            )}
            icon={
              showConfirmPassword ? (
                <Eye
                  className="text-base-500 size-4 cursor-pointer"
                  onClick={() => setShowConfirmPassword(false)}
                />
              ) : (
                <EyeOff
                  className="text-base-500 size-4 cursor-pointer"
                  onClick={() => setShowConfirmPassword(true)}
                />
              )
            }
            type={showConfirmPassword ? 'text' : 'password'}
            iconPosition="right"
          />

          <div className="mt-6">
            <Button.Root
              type="submit"
              size="lg"
              block
              disabled={isPending}
              isLoading={isPending}
            >
              {t('forgot_password.new_password.button.text')}
            </Button.Root>
          </div>

          <Link
            to={ROUTES.LOGIN.SIGN_IN}
            className="text-brand-400 text-sm flex gap-2 items-center justify-center mt-4"
          >
            <MoveLeft className="text-brand-600 size-4 stroke-2" />
            {t('forgot_password.new_password.cancel')}
          </Link>
        </form>
      </FormProvider>

      <div className="flex flex-col gap-4">
        <button
          onClick={() =>
            handleModal({
              title: t('common:app_connection.terms.title'),
              description: (
                <div className="bg-gray-50 p-4 rounded-md border max-h-[400px] overflow-y-auto">
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {t('common:app_connection.terms.content')}
                  </div>
                </div>
              ),
              confirmText: t('common:app_connection.buttons.close'),
              showButtons: true,
              onConfirm: resetModal,
            })
          }
          className="block text-brand-400 text-sm text-center hover:underline"
        >
          {t('sign_in.footer.terms_of_use.text')}
        </button>
        <p className="text-base-400 font-light text-xs text-center">
          {t('sign_in.footer.copyright.text')}
        </p>
      </div>
    </div>
  )
}
